import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronRight, ChevronLeft, X, LayoutDashboard, Video, CheckSquare, 
  AlertTriangle, FileText, Sparkles, ArrowRight, RotateCcw
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TOUR_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to D3 Command',
    description: 'Turn meetings into execution automatically. This interactive demo shows how AI converts conversations into tasks, risks, and decisions.',
    target: null,
    icon: Sparkles,
    isModal: true,
  },
  {
    id: 'dashboard',
    title: 'Real-Time Dashboard',
    description: 'Track tasks, risks, and decisions in real-time. Key metrics update live as meetings are processed and actions are completed.',
    target: '[data-testid="nav-dashboard"]',
    navTo: 'dashboard',
    icon: LayoutDashboard,
  },
  {
    id: 'meetings',
    title: 'AI Meeting Intelligence',
    description: 'AI transcribes and extracts key insights instantly. Upload audio or paste a transcript — get summaries, decisions, and action items in seconds.',
    target: '[data-testid="nav-meetings"]',
    navTo: 'meetings',
    icon: Video,
  },
  {
    id: 'tasks',
    title: 'Automated Task Management',
    description: 'Tasks are automatically created and assigned from meeting content. Track them through Backlog, In Progress, Review, and Completed stages.',
    target: '[data-testid="nav-tasks"]',
    navTo: 'tasks',
    icon: CheckSquare,
  },
  {
    id: 'risks',
    title: 'Risk & Compliance Tracking',
    description: 'Every action is tracked for compliance. AI detects risks with severity levels and tracks mitigation in real-time.',
    target: '[data-testid="nav-risk"]',
    navTo: 'risk',
    icon: AlertTriangle,
  },
  {
    id: 'complete',
    title: "You're ready to transform your operations.",
    description: 'D3 Command turns every meeting into measurable progress. Start your free trial or request a personalized demo.',
    target: null,
    icon: Sparkles,
    isModal: true,
    isFinal: true,
  },
];

export const OnboardingTour = ({ onNavigate }) => {
  const { user, isDemo } = useAuth();
  const [active, setActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const [spotlight, setSpotlight] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const tooltipRef = useRef(null);

  const step = TOUR_STEPS[currentStep];
  const totalSteps = TOUR_STEPS.length;

  // Fetch onboarding status on mount
  useEffect(() => {
    if (!user) return;
    const fetchStatus = async () => {
      try {
        const token = localStorage.getItem('d3_token');
        const res = await fetch(`${API_URL}/api/auth/onboarding`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.status === 'not_started') {
            setActive(true);
            setCurrentStep(0);
          }
        }
      } catch (e) {
        console.error('Onboarding fetch error:', e);
      }
      setLoaded(true);
    };
    fetchStatus();
  }, [user]);

  const saveProgress = useCallback(async (status, stepIdx) => {
    try {
      const token = localStorage.getItem('d3_token');
      await fetch(`${API_URL}/api/auth/onboarding`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, currentStep: stepIdx }),
      });
    } catch (e) {
      console.error('Onboarding save error:', e);
    }
  }, []);

  // Position tooltip near target element
  useEffect(() => {
    if (!active || !step) return;
    if (!step.target || step.isModal) {
      setSpotlight(null);
      setTooltipPos({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)', position: 'fixed' });
      return;
    }

    if (step.navTo && onNavigate) {
      onNavigate(step.navTo);
    }

    const timer = setTimeout(() => {
      const el = document.querySelector(step.target);
      if (!el) {
        setSpotlight(null);
        setTooltipPos({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)', position: 'fixed' });
        return;
      }

      const rect = el.getBoundingClientRect();
      const pad = 8;
      setSpotlight({
        top: rect.top - pad,
        left: rect.left - pad,
        width: rect.width + pad * 2,
        height: rect.height + pad * 2,
      });

      const tooltipW = 400;
      const tooltipH = 280;
      let top = rect.top;
      let left = rect.right + 20;

      if (left + tooltipW > window.innerWidth - 20) {
        left = rect.left;
        top = rect.bottom + 16;
      }
      if (top + tooltipH > window.innerHeight - 20) {
        top = Math.max(20, rect.top - tooltipH - 16);
      }

      setTooltipPos({ top, left, position: 'fixed' });
    }, 250);

    return () => clearTimeout(timer);
  }, [active, currentStep, step, onNavigate]);

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      const next = currentStep + 1;
      setCurrentStep(next);
      saveProgress('in_progress', next);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    setActive(false);
    saveProgress('skipped', currentStep);
  };

  const handleComplete = () => {
    setActive(false);
    saveProgress('completed', totalSteps - 1);
    if (onNavigate) onNavigate('dashboard');
  };

  const handleStartTrial = () => {
    setActive(false);
    saveProgress('completed', totalSteps - 1);
    window.history.pushState({}, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
    // Small delay then scroll to pricing
    setTimeout(() => {
      const el = document.querySelector('#pricing');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  const restart = useCallback(() => {
    setCurrentStep(0);
    setActive(true);
    saveProgress('in_progress', 0);
    if (onNavigate) onNavigate('dashboard');
  }, [saveProgress, onNavigate]);

  useEffect(() => {
    window.__d3RestartTour = restart;
    return () => { delete window.__d3RestartTour; };
  }, [restart]);

  if (!loaded || !active || !step) return null;

  const StepIcon = step.icon;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100]" data-testid="onboarding-tour">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/70 backdrop-blur-[3px]"
          onClick={handleSkip}
        />

        {/* Spotlight cutout */}
        {spotlight && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute rounded-xl ring-2 ring-indigo-400/60 bg-transparent z-[101]"
            style={{
              top: spotlight.top,
              left: spotlight.left,
              width: spotlight.width,
              height: spotlight.height,
              boxShadow: '0 0 0 9999px rgba(0,0,0,0.7)',
            }}
          />
        )}

        {/* Tooltip / Modal card */}
        <motion.div
          ref={tooltipRef}
          key={step.id}
          initial={{ opacity: 0, scale: 0.92, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: 16 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className={`z-[102] rounded-2xl bg-[#0d1221] border border-white/10 shadow-2xl ${
            step.isModal ? 'w-[440px]' : 'w-[400px]'
          }`}
          style={step.isModal ? { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' } : tooltipPos}
          data-testid="onboarding-tooltip"
        >
          {/* Progress bar */}
          <div className="h-1 bg-white/5 rounded-t-2xl overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-indigo-500 to-cyan-500"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>

          <div className="p-6">
            {/* Icon + step counter */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500/20 to-cyan-500/20 flex items-center justify-center flex-shrink-0 border border-indigo-500/20">
                  <StepIcon className="w-5 h-5 text-indigo-400" />
                </div>
                <div>
                  <p className="text-xs text-indigo-400 font-semibold tracking-wide uppercase" data-testid="tour-step-counter">
                    {currentStep + 1} / {totalSteps}
                  </p>
                  <h3 className="text-white font-semibold text-lg leading-tight mt-0.5" data-testid="tour-step-title">{step.title}</h3>
                </div>
              </div>
              <button
                onClick={handleSkip}
                className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                title="Skip tour"
                data-testid="tour-skip-btn"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            {/* Description */}
            <p className="text-gray-400 text-sm leading-relaxed mb-6" data-testid="tour-step-description">{step.description}</p>

            {/* Final step CTAs */}
            {step.isFinal ? (
              <div className="space-y-3">
                <button
                  onClick={handleStartTrial}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-indigo-500 to-cyan-500 text-white text-sm font-semibold hover:opacity-90 transition-opacity"
                  data-testid="tour-start-trial-btn"
                >
                  Start Free Trial
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={handleComplete}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 text-gray-300 text-sm font-medium hover:bg-white/10 transition-colors border border-white/10"
                  data-testid="tour-continue-exploring-btn"
                >
                  Continue Exploring
                </button>
              </div>
            ) : (
              /* Standard navigation */
              <div className="flex items-center justify-between">
                <button
                  onClick={handleSkip}
                  className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
                  data-testid="tour-skip-text-btn"
                >
                  Skip tour
                </button>
                <div className="flex items-center gap-2">
                  {currentStep > 0 && (
                    <button
                      onClick={handlePrev}
                      className="flex items-center gap-1 px-3 py-2 rounded-lg bg-white/5 text-gray-300 text-sm hover:bg-white/10 transition-colors"
                      data-testid="tour-prev-btn"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Back
                    </button>
                  )}
                  <button
                    onClick={handleNext}
                    className="flex items-center gap-1 px-5 py-2 rounded-lg bg-gradient-to-r from-indigo-500 to-cyan-500 text-white text-sm font-semibold hover:opacity-90 transition-opacity"
                    data-testid="tour-next-btn"
                  >
                    {currentStep === 0 ? 'Start Tour' : 'Next'}
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
